import React from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Drawer from "@mui/material/Drawer";
import LoadingButton from "@mui/lab/LoadingButton";
import RightworkSelect from "../../../components/shared/RightworkSelect";
import DrawerHeader from "../requests/shared/helpers/DrawerHeader";
import { API_update_onboarding_preferences } from "../../../constants";
import { postRightworkAPI } from "../requests/utilities/requests";

function conceptSignature(concepts) {
  return JSON.stringify({ mode: concepts.mode, groups: (concepts.groups || [])
    .filter(group => group.store_ids.length)
    .map(group => ({ name: concepts.mode === "one" ? "" : group.name.trim(), store_ids: [...group.store_ids].sort((a, b) => a - b) }))
    .sort((a, b) => a.store_ids[0] - b.store_ids[0]) });
}

export default function CompanyConceptsDrawer({ open, onClose, summary, onSaved }) {
  const [savedSignature, setSavedSignature] = React.useState(null);
  const [mode, setMode] = React.useState("multiple");
  const [groups, setGroups] = React.useState([]);
  const [editing, setEditing] = React.useState(null);
  const [selection, setSelection] = React.useState(null);
  const selectionRef = React.useRef(null);
  const [deleting, setDeleting] = React.useState(null);
  const [moveTo, setMoveTo] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const locations = summary?.storeSummaries || [];
  const initializedCompany = React.useRef(null);
  React.useEffect(() => {
    if (!open) {
      initializedCompany.current = null;
      return;
    }
    const companyId = summary?.company?.id;
    if (companyId == null || initializedCompany.current === companyId) return;
    // Polling updates the summary while the drawer is open. Seed each editing
    // session once so a refresh cannot overwrite the user's mode or group edits.
    initializedCompany.current = companyId;
    const concepts = summary?.company?.concepts;
    setSavedSignature(concepts?.reviewed ? conceptSignature(concepts) : null);
    setMode(concepts?.mode || "multiple");
    setGroups((concepts?.groups || []).map((group, index) => ({ ...group, key: String(index) })));
    setEditing(null);
    setSelection(null);
    selectionRef.current = null;
    setDeleting(null);
    setError("");
  }, [open, summary]);
  function chooseMode(value) {
    setMode(value);
    setEditing(null);
  }
  const assigned = new Set(groups.flatMap(group => group.store_ids));
  const unassigned = locations.filter(row => !assigned.has(row.store_id));
  const invalid = mode === "multiple" && (unassigned.length > 0 || groups.some(group => group.store_ids.length && !group.name.trim()));
  function assignLocations(key, ids) {
    setGroups(current => current.map(group => ({ ...group,
      store_ids: group.key === key ? ids : group.store_ids.filter(id => !ids.includes(id)),
    })));
  }

  function stageSelection(key, ids) {
    const draft = { key, ids: [...ids] };
    selectionRef.current = draft;
    setSelection(draft);
  }
  function commitSelection() {
    const draft = selectionRef.current;
    if (draft) assignLocations(draft.key, draft.ids);
    selectionRef.current = null;
    setSelection(null);
  }
  function deleteConcept(group) {
    if (!group.store_ids.length) {
      setGroups(current => current.filter(item => item.key !== group.key));
      setEditing(null);
    } else {
      setDeleting(group.key);
      setMoveTo("");
    }
  }
  function confirmDelete() {
    if (!groups.some(group => group.key === moveTo && group.key !== deleting)) return;
    setGroups(current => {
      const removed = current.find(group => group.key === deleting);
      return current.filter(group => group.key !== deleting).map(group => group.key === moveTo
        ? { ...group, store_ids: [...new Set([...group.store_ids, ...(removed?.store_ids || [])])] } : group);
    });
    setDeleting(null);
    setEditing(null);
  }

  const draftConcepts = { mode, groups: mode === "one"
    ? [{ name: summary?.company?.company_name, store_ids: locations.map(row => row.store_id) }]
    : groups.filter(group => group.store_ids.length).map(({ name, store_ids }) => ({ name, store_ids })) };
  const hasChanges = savedSignature !== conceptSignature(draftConcepts);

  async function save() {
    setSaving(true); setError("");
    try {
      await postRightworkAPI(API_update_onboarding_preferences, {
        company_id: summary.company.id,
        concepts: draftConcepts,
      });
      await onSaved?.(); onClose();
    } catch (err) {
      setError(err?.response?.data?.system?.message || "Unable to save concepts. Check the names and location assignments.");
    } finally { setSaving(false); }
  }
  return <Drawer open={open} anchor="right" onClose={saving ? undefined : onClose}>
    <div className="flex h-full w-[560px] max-w-[100vw] flex-col bg-white">
      <DrawerHeader title="Review your concepts" onClose={saving ? undefined : onClose} />
      <div className="flex-1 overflow-y-auto p-6">
        <p className="mb-4 text-sm text-slate-600">Do your locations share one concept or represent different brands?</p>
        <fieldset disabled={saving} className="space-y-5">
          <div className="flex rounded-xl border border-slate-200 bg-slate-50 p-1" role="group" aria-label="Company concepts">
            {[["one", "One concept"], ["multiple", "Multiple concepts"]].map(([value, label]) => <button key={value} type="button" aria-pressed={mode === value} onClick={() => chooseMode(value)} className={`flex-1 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 ${mode === value ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>{label}</button>)}
          </div>
          {mode === "one" ? <div>
            <h2 className="text-base font-semibold text-slate-900">{summary?.company?.company_name}</h2>
            <p className="mt-1 text-sm text-slate-500">All {locations.length} locations share this concept.</p>
          </div> : <div>
            <div className="divide-y divide-slate-200">
              {groups.map((group, index) => <div key={group.key} className="py-4 first:pt-0">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-base font-semibold text-slate-900">{group.name || "New concept"}</h2>
                  <div className="flex shrink-0 items-center gap-1">
                    {editing === group.key && <button type="button" aria-label={`Delete ${group.name || "new concept"}`} title="Delete concept" disabled={groups.length === 1 && group.store_ids.length > 0} onClick={() => deleteConcept(group)} className="inline-flex h-9 w-9 items-center justify-center rounded-lg leading-none text-red-600 hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 disabled:opacity-40">
                      <DeleteOutlineIcon fontSize="small" sx={{ display: "block" }} />
                    </button>}
                  <button type="button" onClick={() => setEditing(editing === group.key ? null : group.key)} className="inline-flex h-9 items-center justify-center rounded-lg px-2 text-sm font-medium leading-none text-violet-700 hover:bg-violet-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600" aria-label={`Edit ${group.name || "new concept"}`}>{editing === group.key ? "Done" : "Edit"}</button>
                  </div>
                </div>
                {editing === group.key ? <div className="mt-3 space-y-3">
                  <div>
                    <label htmlFor={`concept-name-${index}`} className="mb-1 block text-sm text-slate-600">Concept name</label>
                    <input id={`concept-name-${index}`} value={group.name} maxLength={120} onChange={event => setGroups(current => current.map(item => item.key === group.key ? { ...item, name: event.target.value } : item))} className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-violet-600 focus:ring-1 focus:ring-violet-600" />
                  </div>
                  <div>
                    <label id={`concept-locations-${index}-label`} htmlFor={`concept-locations-${index}`} className="mb-1 block text-sm text-slate-600">Locations</label>
                    <RightworkSelect id={`concept-locations-${index}`} multiple disabled={saving} value={selection?.key === group.key ? selection.ids : group.store_ids} onOpen={() => stageSelection(group.key, group.store_ids)} onClose={commitSelection} renderValue={ids => `${ids.length} locations selected`} options={locations.map(location => ({ value: location.store_id, label: location.store_name }))} onChange={event => stageSelection(group.key, event.target.value)} />
                  </div>
                  {deleting === group.key && <div className="space-y-3 rounded-lg bg-slate-50 p-3">
                    <label id={`delete-concept-${index}-label`} htmlFor={`delete-concept-${index}`} className="block text-sm text-slate-600">Move these locations to</label>
                    <RightworkSelect id={`delete-concept-${index}`} value={moveTo} onChange={event => setMoveTo(event.target.value)} options={[{ value: "", label: "Choose a concept", disabled: true }, ...groups.filter(item => item.key !== group.key).map(item => ({ value: item.key, label: item.name || "Unnamed concept" }))]} />
                    <div className="flex gap-3">
                      <button type="button" disabled={!moveTo} onClick={confirmDelete} className="rounded-lg px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-40">Move locations and delete</button>
                      <button type="button" onClick={() => setDeleting(null)} className="rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
                    </div>
                  </div>}
                  {groups.length === 1 && <p className="text-xs text-slate-500">Keep one concept, or add another to move these locations into.</p>}
                </div> : <ul className="mt-2 space-y-1 text-sm text-slate-500">{locations.filter(location => group.store_ids.includes(location.store_id)).map(location => <li key={location.store_id}>{location.store_name}</li>)}{!group.store_ids.length && <li>No locations assigned</li>}</ul>}
              </div>)}
            </div>
            <div className={groups.length ? "border-t border-slate-200 pt-4" : ""}>
            <button type="button" onClick={() => { const key = `new-${Date.now()}`; setGroups(current => [...current, { key, name: "", store_ids: [] }]); setEditing(key); }} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600">Add new concept</button>
            </div>
            {unassigned.length > 0 && <p role="status" className="mt-3 text-sm text-amber-700">Assign a concept to: {unassigned.map(row => row.store_name).join(", ")}</p>}
          </div>}
        </fieldset>
        {error && <p role="alert" className="mt-4 text-sm text-red-600">{error}</p>}
      </div>
      {hasChanges && <div className="border-t border-slate-200 p-6"><LoadingButton loading={saving} disabled={!locations.length || invalid} onClick={save} variant="contained" fullWidth
        sx={{ textTransform: "none", borderRadius: "8px", paddingY: "10px", backgroundColor: "#6d28d9", fontSize: "15px", "&:hover": { backgroundColor: "#5b21b6" }, "&.Mui-disabled": { backgroundColor: "#e2e8f0", color: "#94a3b8" } }}>Save</LoadingButton></div>}
    </div>
  </Drawer>;
}
